import { useMemo } from "react";
import { X } from "lucide-react";
import { useFieldLayerList, useSoilSamples } from "../../hooks/queries";
import { pointCoords, rippleLayout, RIPPLE_S } from "../../lib/idwRipple";
import { Modal } from "../../ui/Modal";

/*
 *
 * verbatim: title "Running IDW Interpolation"; NO Customize; the notice at the bottom "running in
 * background, you can close this window"; never "points ready — see below"; no waterways/distance
 * layers — "just show small circles expanding from the sample locations like ripples … start at
 * each point, expand, and then restart in a loop until the user closes the window", with the sample
 * locations read off the POINTS LAYER, no prepared visualization layer. Same pop-up frame as every
 * other animation; the X closes the WINDOW only — the IDW run was dispatched before this opened and
 * is not reachable from here (no callback exists that could reach it).
 */
export function IdwRippleWindow({
  fieldId,
  fieldCount,
  points,
  eventId,
  onClose,
}: {
  fieldId: string;
  fieldCount: number;
  /* * the PICKED points layer — the sample locations the interpolation is running from */
  points: GeoJSON.FeatureCollection | null;
  /* * the sample event being mapped — when no picked layer geometry is in hand, its samples are the locations */
  eventId?: string | null;
  onClose: () => void;
}) {
  // fallback locations: the event's own samples (a results-bearing event always has them)
  const { data: samples } = useSoilSamples(points ? null : fieldId);
  const eventPoints = useMemo<GeoJSON.FeatureCollection | null>(() => {
    if (points || !samples) return null;
    const mine = eventId
      ? samples.filter((s) => (s.sample_event_id ?? null) === eventId)
      : samples;
    return {
      type: "FeatureCollection",
      features: mine.map((s) => ({
        type: "Feature",
        properties: { num: s.sample_id ?? s.point_id },
        geometry: { type: "Point", coordinates: [s.lon, s.lat] },
      })),
    };
  }, [points, samples, eventId]);
  const { data: listed } = useFieldLayerList(fieldId);
  const boundary = (listed?.document.boundary ??
    null) as GeoJSON.GeoJSON | null;
  const coords = useMemo(
    () => pointCoords(points ?? eventPoints),
    [points, eventPoints],
  );
  const layout = useMemo(
    () =>
      rippleLayout(
        coords,
        (boundary ?? null) as
          GeoJSON.Geometry | GeoJSON.Feature | GeoJSON.FeatureCollection | null,
      ),
    [coords, boundary],
  );

  return (
    <Modal>
      <div
        className="fixed inset-0 z-overlay flex items-center justify-center bg-black/60 p-4"
        role="status"
        aria-live="polite"
      >
        <div className="relative flex w-full max-w-lg flex-col gap-2 rounded-lg bg-surface p-4 text-fg shadow-2xl">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-2 top-2 rounded p-1 text-muted hover:bg-hover hover:text-fg"
          >
            <X size={16} />
          </button>
          <h2 className="pr-6 text-center text-[15px] font-semibold">
            Running IDW Interpolation
          </h2>
          <p className="text-center text-[11px] text-muted">
            Every cell takes a distance-weighted blend of its nearest samples
            (power 3){fieldCount > 1 ? ` — ${fieldCount} fields` : ""}.
          </p>
          <div className="relative mt-1 aspect-[4/3] w-full overflow-hidden rounded bg-canvas/60">
            {layout ? (
              <svg
                viewBox={`0 0 ${layout.w} ${layout.h}`}
                preserveAspectRatio="xMidYMid meet"
                className="h-full w-full"
                data-testid="idw-ripples"
              >
                {layout.rings.map((r, i) => (
                  <polygon
                    key={`b${i}`}
                    points={r.map(([x, y]) => `${x},${y}`).join(" ")}
                    fill="rgba(34,197,94,0.10)"
                    stroke="rgba(34,197,94,0.75)"
                    strokeWidth={1.5}
                  />
                ))}
                {layout.points.map((p, i) => (
                  <g key={`p${i}`}>
                    {/* two ripples per point, half a period apart, forever */}
                    {[0, 1].map((k) => (
                      <circle
                        key={k}
                        cx={p.x}
                        cy={p.y}
                        r={0}
                        fill="none"
                        stroke="#E09F3E"
                        strokeWidth={1.5}
                        opacity={0}
                        data-testid="ripple"
                      >
                        <animate
                          attributeName="r"
                          from="0"
                          to={String(layout.radius)}
                          dur={`${RIPPLE_S}s`}
                          begin={`${(p.delay + (k * RIPPLE_S) / 2).toFixed(2)}s`}
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          from="0.9"
                          to="0"
                          dur={`${RIPPLE_S}s`}
                          begin={`${(p.delay + (k * RIPPLE_S) / 2).toFixed(2)}s`}
                          repeatCount="indefinite"
                        />
                      </circle>
                    ))}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={2.4}
                      fill="#E09F3E"
                      stroke="#fff"
                      strokeWidth={0.8}
                    />
                  </g>
                ))}
              </svg>
            ) : (
              <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-faint">
                {coords.length
                  ? "Reading this field’s outline…"
                  : "No sample locations to show for this layer."}
              </div>
            )}
            {/* the notice — there from the start; this window has no second act */}
            <div className="pointer-events-none absolute inset-0 flex items-end justify-center pb-3">
              <span className="rounded bg-black/75 px-3 py-1.5 text-center text-[12px] text-white shadow">
                Running in background — you can close this window.
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
